import { View, Text, TouchableOpacity, ScrollView, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import SectionTitle from '@/components/General/SectionTitle';
import { useState, useEffect } from 'react';
import { Query } from '@/utils/react-query/Queries';
import { ActivityIndicator } from 'react-native-paper';
import Checkbox from '@/components/General/inputs/Checkbox';
import { saveQuickPersonFields } from '@/data/organization';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import Toast from 'react-native-toast-message';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';

const BASIC_FIELDS = [
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
    { key: 'gender', label: 'Gender' },
    { key: 'invitedBy', label: 'Invited By' },
    { key: 'address', label: 'Address' },
    { key: 'family', label: 'Family' },
    { key: 'roles', label: 'Roles' },
];

const QuickFormFieldsModal = ({ isOpen, onClose }) => {
    const queryClient = useQueryClient();
    // Custom fields are paid-only — skip the metadata query on Free and strip
    // any stale saved `custom_*` selections before they persist again.
    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);
    const { data: customFields, isLoading: customFieldsLoading } = Query.personCustomFields.all({ options: { enabled: !isFree } });
    const { data: savedFields, isLoading: savedFieldsLoading } = Query.quickPersonFields.get();

    const [selectedFields, setSelectedFields] = useState([]);

    useEffect(() => {
        if (savedFields) {
            setSelectedFields(isFree ? savedFields.filter(k => !k.startsWith('custom_')) : savedFields);
        }
    }, [savedFields, isFree]);

    const saveMutation = useMutation({
        mutationFn: saveQuickPersonFields,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.quickPersonFields.get() });
            Toast.delayShow({ type: 'success', text2: 'Quick form fields saved successfully' });
            onClose();
        },
        onError: () => {
            Toast.delayShow({ type: 'error', text2: 'Failed to save quick form fields' });
        }
    });

    const toggleField = (key) => {
        setSelectedFields(prev => {
            if (prev.includes(key)) {
                return prev.filter(f => f !== key);
            } else {
                return [...prev, key];
            }
        });
    };

    const selectAll = () => {
        const allKeys = [
            ...BASIC_FIELDS.map(f => f.key),
            ...(customFields?.map(cf => `custom_${cf.id}`) || [])
        ];
        setSelectedFields(allKeys);
    };

    const selectNone = () => {
        setSelectedFields([]);
    };

    const handleSave = () => {
        saveMutation.mutate(selectedFields);
    };

    const selectedCount = selectedFields.length;
    const totalCount = BASIC_FIELDS.length + (customFields?.length || 0);
    const isLoading = customFieldsLoading || savedFieldsLoading;

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={onClose}
            title="Customize Quick Form"
            cancelText="Cancel"
            confirmText="Save"
            onConfirm={handleSave}
            loading={saveMutation.isPending}
        >
            <ScrollView showsVerticalScrollIndicator={false}keyboardShouldPersistTaps="handled"
>
                <Text style={styles.description}>
                    Select which fields to show on the quick add person form. First and last name are always required.
                </Text>

                <View style={styles.header}>
                    <Text style={styles.selectedText}>
                        {selectedCount} of {totalCount} fields selected
                    </Text>
                    <View style={styles.quickActions}>
                        <TouchableOpacity onPress={selectAll} style={styles.quickAction}>
                            <Text style={styles.quickActionText}>Select All</Text>
                        </TouchableOpacity>
                        <Text style={styles.divider}>|</Text>
                        <TouchableOpacity onPress={selectNone} style={styles.quickAction}>
                            <Text style={styles.quickActionText}>Select None</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                {isLoading ? (
                    <View style={styles.loadingContainer}>
                        <ActivityIndicator size="small" />
                        <Text style={styles.loadingText}>Loading fields...</Text>
                    </View>
                ) : (
                    <>
                        <View style={styles.section}>
                            <SectionTitle title="Basic Fields" marginLeft={0} marginTop={0} />
                            {BASIC_FIELDS.map((field) => (
                                <Pressable
                                    key={field.key}
                                    style={styles.fieldRow}
                                    onPress={() => toggleField(field.key)}
                                >
                                    <Checkbox
                                        checked={selectedFields.includes(field.key)}
                                        onChange={() => toggleField(field.key)}
                                        size={18}
                                    />
                                    <Text style={styles.fieldLabel}>{field.label}</Text>
                                </Pressable>
                            ))}
                        </View>

                        {!isFree && (
                        <View style={styles.section}>
                            <SectionTitle title="Custom Fields" marginLeft={0} marginTop={0} />
                            {customFields?.length > 0 ? (
                                customFields.sort((a, b) => a.name.localeCompare(b.name)).map((field) => (
                                    <Pressable
                                        key={field.id}
                                        style={styles.fieldRow}
                                        onPress={() => toggleField(`custom_${field.id}`)}
                                    >
                                        <Checkbox
                                            checked={selectedFields.includes(`custom_${field.id}`)}
                                            onChange={() => toggleField(`custom_${field.id}`)}
                                            size={18}
                                        />
                                        <Text style={styles.fieldLabel}>{field.name}</Text>
                                    </Pressable>
                                ))
                            ) : (
                                <Text style={styles.emptyText}>No custom fields configured</Text>
                            )}
                        </View>
                        )}
                    </>
                )}
            </ScrollView>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    description: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginBottom: 16,
        lineHeight: 20,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    selectedText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    quickActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    quickAction: {
        paddingVertical: 4,
        paddingHorizontal: 8,
    },
    quickActionText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.primary,
    },
    divider: {
        color: colors.border,
    },
    section: {
        marginBottom: 20,
    },
    fieldRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 10,
        paddingHorizontal: 12,
        backgroundColor: colors.surface,
        borderRadius: 8,
        marginBottom: 6,
        borderWidth: 1,
        borderColor: colors.border,
    },
    fieldLabel: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text,
        flex: 1,
        paddingLeft: 10,
    },
    loadingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 20,
        gap: 10,
    },
    loadingText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    emptyText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        paddingVertical: 16,
    },
}));

export default QuickFormFieldsModal;
